/**
 * Task Filtering and Sorting Utilities
 * Smart filtering: "Assigned to Me", "Open Tasks", "Bugs"
 * Heuristic sorting: Due Date (ASC) to surface immediate deadlines
 */

import { Task, User, Project } from '../types';

/**
 * Read task ordering from known optional fields.
 * Order is considered only when finite and >= 0.
 */
export function getTaskOrder(task: Task): number | null {
  const rawCandidates: unknown[] = [
    (task as Task & { order?: unknown }).order,
    (task as Task & { taskOrder?: unknown }).taskOrder,
    (task as Task & { sortOrder?: unknown }).sortOrder,
    (task as Task & { position?: unknown }).position,
    (task.customFields as { order?: unknown } | undefined)?.order,
  ];

  for (const candidate of rawCandidates) {
    const value = typeof candidate === 'number' ? candidate : Number(candidate);
    if (Number.isFinite(value) && value >= 0) {
      return value;
    }
  }

  return null;
}

/**
 * Prefer explicit task order when present, then use title as a stable fallback.
 */
export function sortByTaskOrder(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const aOrder = getTaskOrder(a);
    const bOrder = getTaskOrder(b);

    if (aOrder !== null && bOrder !== null && aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    if (aOrder !== null && bOrder === null) {
      return -1;
    }

    if (aOrder === null && bOrder !== null) {
      return 1;
    }

    return a.title.localeCompare(b.title);
  });
}

/**
 * Filter tasks assigned to a specific user
 */
export function getAssignedToMeTasks(tasks: Task[], userId: string): Task[] {
  return tasks.filter(task => task.assigneeIds.includes(userId));
}

/**
 * Filter open tasks (not completed, deferred, or not required)
 */
export function getOpenTasks(tasks: Task[]): Task[] {
  const closedStatuses = ['Completed', 'Deferred', 'Not Required'];
  return tasks.filter(task => !closedStatuses.includes(task.status));
}

/**
 * Filter bugs only
 */
export function getBugsTasks(tasks: Task[]): Task[] {
  return tasks.filter(task => task.type === 'Bug');
}

/**
 * Sort tasks by due date (ASC) to surface immediate deadlines
 */
export function sortByDueDate(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const aDate = a.targetCompletionDate ? new Date(a.targetCompletionDate).getTime() : Infinity;
    const bDate = b.targetCompletionDate ? new Date(b.targetCompletionDate).getTime() : Infinity;
    return aDate - bDate;
  });
}

/**
 * Sort tasks by priority (descending: P0 > P1 > P2 > etc)
 */
export function sortByPriority(tasks: Task[]): Task[] {
  const priorityOrder: Record<string, number> = {
    'P0': 0, 'P1': 1, 'P2': 2,
    'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3
  };

  return [...tasks].sort((a, b) => {
    const aPriority = priorityOrder[a.priority] ?? 999;
    const bPriority = priorityOrder[b.priority] ?? 999;
    return aPriority - bPriority;
  });
}

/**
 * Get tasks with bugs at the top
 */
export function bugsFirst(tasks: Task[]): Task[] {
  const bugs = tasks.filter(t => t.type === 'Bug');
  const nonBugs = tasks.filter(t => t.type !== 'Bug');
  return [...bugs, ...nonBugs];
}

/**
 * Apply smart default filtering: "Assigned to Me" + open tasks
 * Optionally separate bugs into their own section
 */
export function applySmartDefaults(
  tasks: Task[],
  userId: string,
  options: { separateBugs?: boolean } = {}
): { allTasks: Task[], bugTasks?: Task[] } {
  let assignedTasks = getAssignedToMeTasks(tasks, userId);
  let openTasks = getOpenTasks(assignedTasks);
  let sorted = sortByDueDate(openTasks);

  if (options.separateBugs) {
    const bugs = sorted.filter(t => t.type === 'Bug');
    const nonBugs = sorted.filter(t => t.type !== 'Bug');
    return {
      allTasks: nonBugs,
      bugTasks: bugs
    };
  }

  return {
    allTasks: sorted
  };
}

/**
 * Filter tasks visible to external partners (only their assigned tasks)
 */
export function filterTasksForExternalPartner(tasks: Task[], userId: string): Task[] {
  return getAssignedToMeTasks(tasks, userId);
}

/**
 * Filter tasks that should be visible based on user role in project
 */
export function filterTasksForUserRole(
  tasks: Task[],
  userId: string,
  userRole: string,
  project: Project
): Task[] {
  if (userRole === 'external_partner') {
    return filterTasksForExternalPartner(tasks, userId);
  }

  // Other roles see all project tasks
  return tasks;
}

/**
 * Apply information density settings
 * High density (desktop): more visible rows, reduced padding
 * Reduced height (mobile): fewer visible rows, compact display
 */
export interface DensitySettings {
  maxVisibleRows: number; // Max rows visible without scrolling
  rowHeight: string; // CSS height value
  padding: string; // CSS padding value
  fontSize: string; // CSS font-size value
}

export function getDensitySettings(
  viewport: 'desktop' | 'mobile',
  density: 'high' | 'normal' | 'comfortable' = 'normal'
): DensitySettings {
  if (viewport === 'mobile') {
    return {
      maxVisibleRows: 4,
      rowHeight: '2.5rem',
      padding: 'var(--widget-padding-mobile)',
      fontSize: '0.875rem'
    };
  }

  // Desktop
  if (density === 'high') {
    return {
      maxVisibleRows: 10,
      rowHeight: '2rem',
      padding: 'var(--widget-padding-desktop)',
      fontSize: '0.875rem'
    };
  }

  return {
    maxVisibleRows: 8,
    rowHeight: '2.25rem',
    padding: 'var(--spacing-normal)',
    fontSize: '0.9375rem'
  };
}

/**
 * Paginate tasks for spreadsheet/list views
 */
export function paginateTasks(
  tasks: Task[],
  page: number = 1,
  pageSize: number = 10
): { tasks: Task[], totalPages: number, currentPage: number } {
  const totalPages = Math.ceil(tasks.length / pageSize);
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    tasks: tasks.slice(start, end),
    totalPages,
    currentPage: page
  };
}

/**
 * Search tasks by title/description
 */
export function searchTasks(tasks: Task[], query: string): Task[] {
  if (!query.trim()) return tasks;

  const lowerQuery = query.toLowerCase();
  return tasks.filter(task =>
    task.title.toLowerCase().includes(lowerQuery) ||
    task.description.toLowerCase().includes(lowerQuery)
  );
}
